import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Video, AlertCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventStream() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jitsiLoaded, setJitsiLoaded] = useState(false);

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  useEffect(() => {
    if (event && event.jitsi_room_name) {
      loadJitsiScript();
    }
  }, [event]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      console.error('Error fetching event:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadJitsiScript = () => {
    // Check if Jitsi script is already loaded
    if (window.JitsiMeetExternalAPI) {
      initializeJitsi();
      return;
    }

    // Load Jitsi Meet API script
    const script = document.createElement('script');
    script.src = 'https://meet.jit.si/external_api.js';
    script.async = true;
    script.onload = () => {
      setJitsiLoaded(true);
      initializeJitsi();
    };
    document.body.appendChild(script);
  };

  const initializeJitsi = () => {
    if (!event || !event.jitsi_room_name) return;

    const domain = 'meet.jit.si';
    const options = {
      roomName: event.jitsi_room_name,
      width: '100%',
      height: '100%',
      parentNode: document.querySelector('#jitsi-container'),
      configOverwrite: {
        prejoinPageEnabled: false,
        startWithAudioMuted: true,
        startWithVideoMuted: false,
      },
      interfaceConfigOverwrite: {
        SHOW_JITSI_WATERMARK: false,
        SHOW_WATERMARK_FOR_GUESTS: false,
        SHOW_BRAND_WATERMARK: false,
        BRAND_WATERMARK_LINK: '',
        TOOLBAR_BUTTONS: [
          'microphone', 'camera', 'closedcaptions', 'desktop', 'fullscreen',
          'fodeviceselection', 'hangup', 'profile', 'chat', 'recording',
          'livestreaming', 'etherpad', 'sharedvideo', 'settings', 'raisehand',
          'videoquality', 'filmstrip', 'invite', 'feedback', 'stats', 'shortcuts',
          'tileview', 'videobackgroundblur', 'download', 'help', 'mute-everyone',
        ],
        HIDE_INVITE_MORE_HEADER: false,
      },
      // Enable recording
      recordingService: {
        enabled: true,
        sharingEnabled: true
      },
      userInfo: {
        displayName: 'Partecipante'
      }
    };

    const api = new window.JitsiMeetExternalAPI(domain, options);

    // Event listeners
    api.addEventListener('videoConferenceJoined', () => {
      console.log('User joined the conference');
    });

    api.addEventListener('videoConferenceLeft', () => {
      console.log('User left the conference');
    });

    // Recording events
    api.addEventListener('recordingStatusChanged', (status) => {
      console.log('Recording status:', status);
      if (status.on) {
        console.log('Recording started');
      } else {
        console.log('Recording stopped');
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-600">Caricamento streaming...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Evento non trovato</h2>
            <p className="text-gray-600">L'evento richiesto non esiste.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!event.use_integrated_streaming || !event.jitsi_room_name) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Video className="w-12 h-12 text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Streaming non disponibile</h2>
            <p className="text-gray-600 mb-4">
              Questo evento non utilizza lo streaming integrato.
            </p>
            {event.streaming_url && (
              <a
                href={event.streaming_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Vai al link streaming esterno →
              </a>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-900">
      {/* Header */}
      <div className="bg-white shadow-md p-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900">{event.title}</h1>
          <p className="text-sm text-gray-600">Streaming Live - Powered by Jitsi Meet</p>
        </div>
      </div>

      {/* Jitsi Container */}
      <div className="flex-1 relative">
        <div id="jitsi-container" className="absolute inset-0" />
      </div>

      {/* Footer */}
      <div className="bg-gray-800 text-white p-3 text-center text-sm">
        <p>🎥 ZonePass Integrated Streaming • Evento: {event.title}</p>
      </div>
    </div>
  );
}
